import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ChartBar, MagnifyingGlass, DownloadSimple, Receipt, X, Warning, ArrowClockwise } from "@phosphor-icons/react";

const LTYPE = { in: "Masuk", adjust: "Penyesuaian", dispense: "Penyerahan", reverse: "Retur/Pembalikan" };
const inC = "w-full rounded-xl border border-pink-100 bg-[#FDFBFE] px-3 py-2 text-sm";

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function StokMutasi() {
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(todayStr());
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null); // { med, data, type }

  const load = useCallback(() => {
    setLoading(true);
    api.get("/pharmacy/stock/report", { params: { start, end, q, page, page_size: 50 } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [start, end, q, page]);

  useEffect(() => { document.title = "Stok Farmasi & Mutasi Obat — HalloGBS"; }, []);
  useEffect(() => { load(); }, [load]);

  const openCard = async (row, mtype = "") => {
    try {
      const { data: d } = await api.get(`/pharmacy/medications/${row.medication_id}/ledger`, { params: { start, end, type: mtype } });
      setCard({ med: row, data: d, type: mtype });
    } catch (e) { toast.error(apiError(e)); }
  };

  const doExport = async () => {
    try {
      const res = await api.get("/pharmacy/stock/report/export", { params: { start, end, q }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `Stok_Mutasi_Obat_${start}_${end}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Excel diunduh");
    } catch (e) { toast.error(apiError(e)); }
  };

  const t = data?.totals || {};
  const rows = data?.rows || [];
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="space-y-5" data-testid="stok-mutasi">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-[#4C1D95]"><ChartBar size={24} weight="duotone" className="text-[#DB2777]" /> Stok Farmasi & Mutasi Obat</h1>
          <p className="mt-1 text-xs text-slate-500">Laporan per-periode dari mesin stok obat (medications/batch/ledger). Hanya mencakup obat, belum seluruh inventori barang.</p>
        </div>
        <button data-testid="stok-export-btn" onClick={doExport} className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 active:scale-95"><DownloadSimple size={16} /> Excel</button>
      </div>

      <div className="grid gap-3 rounded-2xl border border-pink-100 bg-white p-4 sm:grid-cols-4">
        <label className="text-xs font-bold text-slate-600">Dari Tanggal<input data-testid="stok-start" type="date" className={inC} value={start} onChange={(e) => { setPage(1); setStart(e.target.value); }} /></label>
        <label className="text-xs font-bold text-slate-600">Sampai Tanggal<input data-testid="stok-end" type="date" className={inC} value={end} onChange={(e) => { setPage(1); setEnd(e.target.value); }} /></label>
        <label className="text-xs font-bold text-slate-600 sm:col-span-2">Cari Obat / Kode
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="stok-search" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="nama generik / dagang / kode" className={inC + " pl-9"} />
          </div>
        </label>
      </div>

      <p className="-mt-2 text-[11px] text-slate-400">Filter kategori & lokasi belum tersedia — master obat belum memiliki field tersebut (menunggu keputusan sumber master barang terpadu).</p>

      {data && !data.reconciled && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="stok-recon-warning">
          <p className="flex items-center gap-2 text-sm font-extrabold text-rose-700"><Warning size={18} weight="fill" /> Rekonsiliasi: {data.discrepancies.length} obat selisih (dilaporkan, TIDAK dikoreksi otomatis)</p>
          <div className="mt-1 space-y-0.5 text-xs text-rose-700">
            {data.discrepancies.map((d, i) => (
              <p key={i} data-testid={`recon-${i}`}>{d.nama}: stok kini {d.stok_kini} vs saldo ledger {d.ledger_balance} (selisih {d.selisih > 0 ? "+" : ""}{d.selisih})</p>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-pink-100 bg-white">
        <table className="w-full min-w-[860px] text-sm">
          <thead className="bg-[#FAF7FF] text-left text-xs text-slate-500">
            <tr>
              <th className="p-3">Obat</th>
              <th className="p-3">Satuan</th>
              <th className="p-3 text-right">Stok Awal</th>
              <th className="p-3 text-right">Masuk</th>
              <th className="p-3 text-right">Keluar</th>
              <th className="p-3 text-right">Retur</th>
              <th className="p-3 text-right">Opname (±)</th>
              <th className="p-3 text-right">Stok Akhir</th>
              <th className="p-3 text-right">Stok Kini</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.medication_id} data-testid={`stok-row-${r.medication_id}`} className={`border-t border-pink-50 ${!r.reconciled ? "bg-rose-50/50" : ""}`}>
                <td className="p-3">
                  <button data-testid={`stok-card-btn-${r.medication_id}`} onClick={() => openCard(r)} className="text-left font-bold text-[#6A38C7] hover:underline">{r.nama}</button>
                  {r.kode_lokal ? <div className="font-mono text-[10px] text-slate-400">{r.kode_lokal}</div> : null}
                </td>
                <td className="p-3 text-slate-500">{r.satuan || "-"}</td>
                <td className="p-3 text-right" data-testid={`stok-awal-${r.medication_id}`}>{r.stok_awal}</td>
                <td className="p-3 text-right text-emerald-600">{r.masuk || 0}</td>
                <td className="p-3 text-right text-rose-600">{r.keluar || 0}</td>
                <td className="p-3 text-right text-sky-600">{r.retur || 0}</td>
                <td className="p-3 text-right text-amber-600">{r.adj_plus ? `+${r.adj_plus}` : ""}{r.adj_minus ? ` -${r.adj_minus}` : ""}{!r.adj_plus && !r.adj_minus ? "0" : ""}</td>
                <td className="p-3 text-right font-extrabold text-[#4C1D95]" data-testid={`stok-akhir-${r.medication_id}`}>{r.stok_akhir}</td>
                <td className="p-3 text-right">{r.stok_kini}{!r.reconciled && <span className="ml-1 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-600">≠</span>}</td>
                <td className="p-3 text-right"><button onClick={() => openCard(r)} className="rounded-lg bg-[#FCE7F3] p-2 text-[#DB2777]" title="Kartu Stok"><Receipt size={15} /></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-slate-400">{loading ? "Memuat…" : "Tidak ada data pada periode ini."}</td></tr>}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-pink-200 bg-[#FAF7FF] font-extrabold text-slate-700" data-testid="stok-totals">
                <td className="p-3" colSpan={2}>TOTAL ({data.total} obat)</td>
                <td className="p-3 text-right">{t.stok_awal}</td>
                <td className="p-3 text-right text-emerald-700">{t.masuk}</td>
                <td className="p-3 text-right text-rose-700">{t.keluar}</td>
                <td className="p-3 text-right text-sky-700">{t.retur}</td>
                <td className="p-3 text-right text-amber-700">+{t.adj_plus} -{t.adj_minus}</td>
                <td className="p-3 text-right text-[#4C1D95]">{t.stok_akhir}</td>
                <td className="p-3 text-right">{t.stok_kini}</td>
                <td className="p-3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button data-testid="stok-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-600 disabled:opacity-40">Sebelumnya</button>
          <span className="text-sm text-slate-500">Hal {page} / {pages}</span>
          <button data-testid="stok-next" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-600 disabled:opacity-40">Berikutnya</button>
        </div>
      )}

      {card && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setCard(null)}>
          <div data-testid="stok-card-modal" onClick={(e) => e.stopPropagation()} className="my-6 w-full max-w-3xl rounded-3xl bg-white p-6">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-heading text-lg font-extrabold text-[#4C1D95]">Kartu Stok — {card.med.nama}</h2>
              <button onClick={() => setCard(null)}><X size={20} /></button>
            </div>
            <p className="mb-2 text-sm font-bold text-slate-600" data-testid="stok-card-summary">
              Periode {start} s/d {end} · Stok awal: <span className="text-slate-800">{card.data.stok_awal}</span> · Saldo kini: <span className="text-[#7C4DE0]">{card.data.balance}</span>
              {card.data.reconciled
                ? <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">cocok</span>
                : <span className="ml-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-600">selisih</span>}
            </p>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {["", "in", "dispense", "reverse", "adjust"].map((tp) => (
                <button key={tp || "all"} data-testid={`stok-card-filter-${tp || "all"}`} onClick={() => openCard(card.med, tp)}
                  className={`rounded-full px-3 py-1 text-[11px] font-bold ${card.type === tp ? "bg-[#7C4DE0] text-white" : "bg-slate-100 text-slate-600"}`}>
                  {tp ? LTYPE[tp] : "Semua"}
                </button>
              ))}
              <button onClick={() => openCard(card.med, card.type)} className="ml-auto flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold text-slate-600"><ArrowClockwise size={12} /> Muat ulang</button>
            </div>
            <p className="mb-2 text-[11px] text-slate-400">Read-only. Riwayat tidak dapat diedit/dihapus. Referensi resep berupa kode internal (tanpa identitas pasien).</p>
            <div className="overflow-x-auto rounded-xl border border-pink-100">
              <table className="w-full min-w-[720px] text-xs">
                <thead className="bg-[#FAF7FF] text-left text-slate-500"><tr><th className="p-2">Waktu</th><th className="p-2">Jenis</th><th className="p-2">Batch</th><th className="p-2">ED</th><th className="p-2 text-right">Masuk</th><th className="p-2 text-right">Keluar</th><th className="p-2 text-right">Saldo</th><th className="p-2">Petugas</th><th className="p-2">Ref/Alasan</th></tr></thead>
                <tbody>
                  {card.data.entries.map((e, i) => (
                    <tr key={i} data-testid={`stok-card-row-${i}`} className="border-t border-pink-50">
                      <td className="p-2 whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }) : "-"}</td>
                      <td className="p-2 font-bold">{LTYPE[e.type] || e.type}</td>
                      <td className="p-2">{e.batch_no || "-"}</td>
                      <td className="p-2">{e.expiry || "-"}</td>
                      <td className="p-2 text-right text-emerald-600">{e.qty > 0 ? `+${e.qty}` : ""}</td>
                      <td className="p-2 text-right text-rose-600">{e.qty < 0 ? e.qty : ""}</td>
                      <td className="p-2 text-right font-bold">{e.balance_after}</td>
                      <td className="p-2">{e.actor_email || "-"}</td>
                      <td className="p-2">{e.prescription_ref ? `RX-${String(e.prescription_ref).slice(-6)}` : (e.reason || "-")}</td>
                    </tr>
                  ))}
                  {card.data.entries.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-400">Tidak ada pergerakan pada filter ini.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
